import { Component, effect, inject, signal, viewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import dayjs from 'dayjs';
import { MedarbejdereDatasource } from '@features/letpensiondk/letpensiondk-medarbejdere/medarbejdere.datasource';
import { InputText } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { MedarbejdereService } from '@features/letpensiondk/letpensiondk-medarbejdere/medarbejdere.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { FileSelectEvent, FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-letpensiondk-medarbejdere-detail',
  imports: [
    ReactiveFormsModule,
    InputText,
    ButtonModule,
    MessageModule,
    ConfirmDialog,
    FileUpload,
  ],
  providers: [ConfirmationService],
  templateUrl: './letpensiondk-medarbejdere-detail.component.html',
  styleUrl: './letpensiondk-medarbejdere-detail.component.css',
})
export class LetpensiondkMedarbejdereDetailComponent {
  confirmationService = inject(ConfirmationService);
  medarbejdereService = inject(MedarbejdereService);
  datasource = inject(MedarbejdereDatasource);
  form: FormGroup;
  uploader = viewChild.required<FileUpload>('uploader');

  saving = signal(false);

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      navn: ['', { validators: [Validators.required] }],
      stilling: [''],
      tlf: [''],
      email: [''],
      sekvensNr: [''],
      uploadBillede: [],
      oprettet: new FormControl<Date | undefined>(undefined, {
        validators: [Validators.required],
      }),
    });

    effect(() => {
      const medarbejder = this.medarbejdereService.selectedMedarbejder();
      if (!medarbejder) {
        this.form.reset();
        this.form.patchValue({
          oprettet: dayjs().toDate(),
        });
      } else {
        this.form.setValue({
          navn: medarbejder.navn || '',
          stilling: medarbejder.stilling || '',
          tlf: medarbejder.tlf || '',
          email: medarbejder.email || '',
          sekvensNr: medarbejder.sekvensNr || '',
          oprettet: medarbejder.oprettet || '',
          uploadBillede: '',
        });
      }
    });
  }

  async onClickSave() {
    await this.saveChanges();
  }

  async onClickDelete(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Er du sikker?',
      header: 'Slet medarbejder',
      rejectButtonProps: {
        label: 'Fortryd',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Slet',
        severity: 'danger',
      },

      accept: async () => {
        const id = this.medarbejdereService.selectedMedarbejder()?.id;
        if (id) {
          await this.datasource.remove(id);
        }
        this.medarbejdereService.activeTab.set('list');
      },
    });
  }

  async saveChanges() {
    if (this.form.invalid) {
      return;
    }

    this.saving.set(true);

    const medarbejder = this.medarbejdereService.selectedMedarbejder();

    const formData = new FormData();
    Object.keys(this.form.controls).forEach(formControlName => {
      formData.append(formControlName, this.form.get(formControlName)?.value);
    });

    if (medarbejder?.id) {
      formData.append('id', '' + medarbejder.id);
      await this.datasource.updateMultipart(medarbejder.id, formData);
    } else {
      await this.datasource.createMultipart(formData);
    }

    this.uploader().clear();
    this.saving.set(false);
    this.medarbejdereService.activeTab.set('list');
  }

  onFileSelect(event: FileSelectEvent) {
    if (event.currentFiles && event.currentFiles.length > 0) {
      const file = event.currentFiles[0];
      this.form.patchValue({
        uploadBillede: file,
      });
    }
  }

  getBillede() {
    if (
      !this.medarbejdereService.selectedMedarbejder()?.billede &&
      !this.form.value['uploadBillede']
    )
      return null;

    if (this.form.value['uploadBillede']) {
      return this.form.value['uploadBillede'].objectURL;
    } else {
      return (
        'data:image/jpeg;base64,' +
        this.medarbejdereService.selectedMedarbejder()?.billede
      );
    }
  }
}
